import { useDatadog } from '@/auth/datadog';
import { useSentry } from '@/auth/sentry';
import { ApolloProvider } from '@/utils/apollo';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider as JotaiProvider } from 'jotai';
import { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { LoadingBarContainer } from 'react-top-loading-bar';

import { AXLE_TEAL } from './constants/colors';
import { DialogProvider } from './dialogs/DialogManager';
import { AxleErrorBoundary, GlobalErrorBoundary } from './fetching/errorBoundaries';
import { queryClient } from './fetching/queryClient';
import { ReactQueryTopLoadingBar } from './fetching/topLoadingBar';
import { RootRoutes } from './routes';
import { AxleTheme } from './theme';
import { AxleToastContainer } from './toast';
import { envVars } from './utils/envVars';

const enableReactQueryDevTools = envVars.enableReactQueryDevTools;

/* Define all the app-level components, providers, etc. */
export function App() {
    useSentry();
    useDatadog();

    return (
        <>
            <GlobalErrorBoundary>
                {/* Make Jotai use our custom Query client. See: https://jotai.org/docs/integrations/query#referencing-the-same-instance-of-query-client-in-your-project */}
                {/* TODO: Set up Provider to rehydrate using the method linked above, as the current version of Jotai does not accept our previous initialValues implementation. */}
                <JotaiProvider>
                    <ApolloProvider>
                        {/* This is the global error boundary. It catches errors and show an error screen instead.
                         *   Without this, React would unmount the whole component tree and show a blank screen.
                         *
                         * TRY TO HANDLE ERRORS LOCALLY! It's a very bad user experience to see a crash screen. Instead,
                         *  try to handle errors locally. Use an <ErrorBoundary> to wrap your component, and display
                         *  an appropriate fallback there. Send a toast. Etc. Try to leave the page functional. This
                         *  is just a panic screen of last resort.
                         *
                         * Note: This doesn't catch errors in async code by default. If you're using async code,
                         *  you should either use the useAsyncEffect() helper we define in utils/,
                         *  or your code should be inside useQuery or atomWithQuery, which handle errors correctly.
                         *  Otherwise, your error will pass silently.
                         *
                         * This should be the top-level component so it can catch everything inside.
                         */}
                        <AxleErrorBoundary clearAuth={true} reload={true}>
                            {/* Inject Emotion before JSS; needed for upgrade from MUI v4 to v5.
                             *  see: https://mui.com/material-ui/migration/v5-style-changes/#%E2%9C%85-adjust-css-injection-order
                             */}
                            <StyledEngineProvider injectFirst>
                                {/* Provide app-wide Material UI theme. */}
                                <ThemeProvider theme={AxleTheme}>
                                    {/* Kind of like normalize.css, and does things like apply our background color. */}
                                    <CssBaseline />
                                    {/* Custom react-query client. */}
                                    <QueryClientProvider client={queryClient}>
                                        {/* This includes a floating React Query dev tools button on the page during development. */}
                                        {enableReactQueryDevTools && (
                                            <ReactQueryDevtools
                                                initialIsOpen={false}
                                                position={'top-right'}
                                            />
                                        )}

                                        {/* Helmet is used for managing the document head. It is a simple React wrapper around the HTML head tag. */}
                                        <HelmetProvider>
                                            {/* <BrowserRouter/> lets us use declarative routing with the HTML5 History API. */}
                                            <BrowserRouter>
                                                {/* Top-level suspense, to show a blank screen till everything inside has loaded. */}
                                                <Suspense fallback={<></>}>
                                                    {/* The main app UI consists of two elements:
                                                     *  - a global top loading bar, to show if a query is being loaded.
                                                     *  - the current app page based on the browser URL.
                                                     *  - a global toast container
                                                     */}
                                                    <LoadingBarContainer
                                                        props={{ color: AXLE_TEAL, height: 3 }}
                                                    >
                                                        <ReactQueryTopLoadingBar />
                                                        <DialogProvider>
                                                            <div className='App'>
                                                                <RootRoutes />
                                                            </div>
                                                        </DialogProvider>
                                                    </LoadingBarContainer>
                                                    <AxleToastContainer />
                                                </Suspense>
                                            </BrowserRouter>
                                        </HelmetProvider>
                                    </QueryClientProvider>
                                </ThemeProvider>
                            </StyledEngineProvider>
                        </AxleErrorBoundary>
                    </ApolloProvider>
                </JotaiProvider>
            </GlobalErrorBoundary>
        </>
    );
}
